import { defaultAxiosInstance } from '@core/http/config';
import { HttpError, HttpRange, HttpStatusCode, HttpTask } from '@core/http/model';
import { Filter } from '@shared/modules/filter';
import { Range, RangeCursor, RangeResult } from '@shared/modules/range';
import { logSentryHttpError } from '@shared/modules/sentry/utils';
import { removeEmptyString } from '@shared/utils/string';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Lazy, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';

function onError<E = unknown>(err: unknown): HttpError<E> {
  const error = HttpError.fromAxiosError<E>(err as AxiosError<E>);

  if (
    error.status >= 400 &&
    ![
      HttpStatusCode.UNAUTHORIZED,
      HttpStatusCode.FORBIDDEN,
      HttpStatusCode.NOT_FOUND,
      HttpStatusCode.CONFLICT,
    ].includes(error.status)
  ) {
    console.error(error);
    logSentryHttpError(`[http] error ${error.status} on ${O.getOrElse(() => 'unknown')(error.url)} path`, error);
  }

  return error;
}

function transformRequest<R, E>(request: Lazy<Promise<AxiosResponse<R>>>): HttpTask<R, E> {
  return pipe(
    TE.tryCatch(request, err => onError<E>(err)),
    TE.map(res => res.data),
  );
}

function get<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.get(url, config));
}

function getRange<R = unknown, F extends Filter = {}, E = unknown>(
  url: string,
  cursor: RangeCursor,
  filter?: F,
  config?: AxiosRequestConfig,
): HttpRange<R, F, E> {
  return pipe(
    get<RangeResult<R, F>, E>(url, {
      ...config,
      params: {
        ...config?.params,
        ...cursor,
        ...filter,
      },
    }),
    TE.map(Range.fromRangeResult),
  );
}

function filterBody(data: any) {
  return pipe(
    O.fromNullable(data),
    O.filter(data => !(data instanceof FormData)),
    O.map(removeEmptyString),
    O.getOrElse(() => data),
  );
}

function post<R = unknown, E = unknown>(url: string, data?: any, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.post(url, filterBody(data), config));
}

function put<R = unknown, E = unknown>(url: string, data?: any, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.put(url, filterBody(data), config));
}

function del<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E> {
  return transformRequest(() => defaultAxiosInstance.delete(url, config));
}

export const httpService = {
  get,
  getRange,
  post,
  put,
  delete: del,
};
